import { useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { Center, Text3D } from "@react-three/drei";
import { useParams } from "wouter";
import { 
  audioVolumeAtom, 
  languageAtom, 
  portraitLobbySelectionAtom,
  deviceAtom,
  hostAtom,
  clientAtom,
  readyToStartAtom,
  spectatorsAtom,
  teamsAtom,
  resultsAtom
  , minigameRuleAtom
} from "../../GlobalState.jsx";
import useSoundEffectsPlayer from "../../soundPlayers/useSoundEffectsPlayer.jsx";
import translations from "../../dictionaries/translations.js";
import layout from "../../dictionaries/layout.js";
import AudioButtonSection from "./AudioButtonSection.jsx";
import { getSocket } from "../../socket.js";
import { trackEvent } from '../../api';

const socket = getSocket();

export default function ActionSection({ position }) {
  const language = useAtomValue(languageAtom)
  const device = useAtomValue(deviceAtom)
  const selection = useAtomValue(portraitLobbySelectionAtom)
  const readyToStart = useAtomValue(readyToStartAtom)
  const host = useAtomValue(hostAtom)
  const client = useAtomValue(clientAtom)
  const teams = useAtomValue(teamsAtom)
  const spectators = useAtomValue(spectatorsAtom)
  const results = useAtomValue(resultsAtom)
  const minigameRule = useAtomValue(minigameRuleAtom)
  const isHost = host && client && client.socketId === host.socketId
  const params = useParams();
  const { playSoundEffect } = useSoundEffectsPlayer()

  function ShareThisLobbyButton({ position }) {
    function handleSharePointerEnter(e) {
      e.stopPropagation()
    }
    function handleSharePointerLeave(e) {
      e.stopPropagation()
    }
    async function handleSharePointerDown(e) {
      e.stopPropagation()
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Yutnori',
            text: "Let's play a game!",
            url: window.location.href,
          })

          await trackEvent({ surface: 'lobby', action: 'share_lobby', category: 'ui' })
        } catch (err) {
          console.error('Error sharing:', err)
        }
      } else {
        alert('Web Share API is not supported. Copy the URL to share.')
      }
    }

    return <group name='share-this-lobby-button' position={position}>
      <mesh scale={[11.4, 0.01, 1.8]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='orange'/>
      </mesh>
      <mesh scale={[11.3, 0.02, 1.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[11.4, 0.02, 1.8]}
      onPointerEnter={e => handleSharePointerEnter(e)}
      onPointerLeave={e => handleSharePointerLeave(e)}
      onPointerDown={e => handleSharePointerDown(e) }
      >
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='black' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.5}
          height={0.01}
          lineHeight={0.7}>
          {translations.lobby.shareThisLobby[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>
      </Center>
    </group>
  }

  function GuestStartButton({ position }) {
    // looks like the StartGame button but only displays text.
    // thought: pop messages: 'let's go!' for host to see
    function handleStartPointerDown(e) {
        // no-op
    }
    return <group name='host-will-start' position={position}>
      <mesh scale={[11.4, 0.01, 1.8]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='grey'/>
      </mesh>
      <mesh scale={[11.3, 0.02, 1.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[11.4, 0.02, 1.8]}
      onPointerDown={e => handleStartPointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='grey' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.5}
          height={0.01}
          lineHeight={0.7}>
          {translations.lobby.hostWillStart[language]}
          <meshStandardMaterial color='grey'/>
        </Text3D>
      </Center>
    </group>
  }

  function StartGameButton({ position }) {
    const [pressed, setPressed] = useState(false) // for set-dul-hana countdown
    const lastStartGameAtRef = useRef(0)
    async function handleStartPointerDown(e) {
      e.stopPropagation()
      const now = Date.now()
      if (isHost && readyToStart && !pressed && now - lastStartGameAtRef.current >= 500) {
        lastStartGameAtRef.current = now
        setPressed(true)
        socket.emit('gameStartCountdown', { roomId: params.id.toUpperCase(), clientId: client._id })
        await trackEvent({
          surface: 'lobby',
          action: 'start_game',
          category: 'gameplay',
          metadata: {
            players: teams.reduce((count, team) => count + team.players.length, 0),
            hasAi: teams.some((team) => team.players.some((player) => player.type === 'ai')),
            spectators: spectators.length,
            gameNumber: results.length + 1,
            minigameEnabled: minigameRule
          }
        })
      }
    }
    return <group name='start-game-button' position={position}>
      <mesh scale={[11.4, 0.01, 1.8]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (isHost && readyToStart && !pressed)  ? 'orange' : 'grey' }/>
      </mesh>
      <mesh scale={[11.3, 0.02, 1.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[11.4, 0.02, 1.8]}
      onPointerDown={e => handleStartPointerDown(e) }>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='black' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.5}
          height={0.01}
          lineHeight={0.7}>
          { readyToStart ? translations.lobby.startGame[language] : translations.lobby.waitingForCrew[language] }
          <meshStandardMaterial color={ (isHost && readyToStart && !pressed) ? 'orange' : 'grey' }/>
        </Text3D>
      </Center>
    </group>
  }

  return <group position={position}>
    { selection === 'players' && <AudioButtonSection position={layout[device].lobby.lobbySettingsSection.position}/> }
    <ShareThisLobbyButton position={[0, 5, 2.6]}/>
    { !isHost && <GuestStartButton position={[0, 5, 4.6]}/> }
    { isHost && <StartGameButton position={[0, 5, 4.6]}/> } 
  </group>
}
